import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import { addUser, getUserById, updateUser } from "../../services/userService";
import "./AddUserLayer.css";

const AddUserLayer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    role: "",
    password: "",
    status: "active",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  /* =========================
     LOAD USER
  ========================= */
  useEffect(() => {
    if (isEdit) fetchUser();
  }, [id]);

  const fetchUser = async () => {
    try {
      const res = await getUserById(id);
      if (res?.status === "success" && res.data) {
        setForm({
          name: res.data.name || "",
          email: res.data.email || "",
          mobile: res.data.mobile || "",
          role: res.data.role || "",
          password: "",
          status: res.data.status || "active",
        });
      }
    } catch {
      toast.error("Failed to load user");
    }
  };

  /* =========================
     HANDLE CHANGE
  ========================= */
  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  /* =========================
     VALIDATION
  ========================= */
  const validateForm = () => {
    let err = {};

    // Name
    if (!form.name.trim()) {
      err.name = "Name is required";
    }

    // Email
    if (!form.email.trim()) {
      err.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      err.email = "Invalid email format";
    }

    // Mobile
    if (!form.mobile.trim()) {
      err.mobile = "Mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(form.mobile)) {
      err.mobile = "Enter valid 10 digit mobile";
    }

    // Role
    if (!form.role) {
      err.role = "Role is required";
    }

    // Password (only create)
    if (!isEdit && !form.password) {
      err.password = "Password is required";
    }

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  /* =========================
     SUBMIT
  ========================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors");
      return;
    }

    try {
      const payload = {
        user_id: id,
        name: form.name,
        email: form.email,
        mobile: form.mobile,
        role: form.role,
        status: form.status,
      };

      const res = isEdit ? await updateUser(payload) : await addUser(form);

      if (res?.status === "success" || res?.status === true) {
        toast.success(res?.message || (isEdit ? "User Updated Successfully" : "User Created Successfully"));
        setTimeout(() => navigate("/user-list"), 800);
      } else {
        toast.error(res?.message || "Action failed");
      }
    } catch (error) {
      const errMsg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Operation failed";
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-user-wrapper">
      <div className="add-user-card">
        <h5>{isEdit ? "Update User" : "Create User"}</h5>

        <form onSubmit={handleSubmit}>
          <div className="grid">

            {/* NAME */}
            <div className={`form-group ${errors.name ? "has-error" : ""}`}>
              <label>Full Name *</label>
              <div className="input-wrapper">
                {/* <FaUser className="input-icon" /> */}
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Enter full name"
                />
              </div>
              {errors.name && <span className="error-text">{errors.name}</span>}
            </div>

            {/* EMAIL */}
            <div className={`form-group ${errors.email ? "has-error" : ""}`}>
              <label>Email Address *</label>
              <div className="input-wrapper">
                {/* <FaEnvelope className="input-icon" /> */}
                <input
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Enter email"
                />
              </div>
              {errors.email && <span className="error-text">{errors.email}</span>}
            </div>

            {/* MOBILE */}
            <div className={`form-group ${errors.mobile ? "has-error" : ""}`}>
              <label>Mobile Number *</label>
              <div className="input-wrapper">
                {/* <FaPhoneAlt className="input-icon" /> */}
                <input
                  name="mobile"
                  value={form.mobile}
                  onChange={handleChange}
                  placeholder="Enter mobile"
                />
              </div>
              {errors.mobile && <span className="error-text">{errors.mobile}</span>}
            </div>

            {/* ROLE */}
            <div className={`form-group ${errors.role ? "has-error" : ""}`}>
              <label>User Role *</label>
              <div className="input-wrapper">
                {/* <FaUserTie className="input-icon" /> */}
                <select name="role" value={form.role} onChange={handleChange}>
                  <option value="">Select Role</option>
                  {/* <option value="user">User</option> */}
                  <option value="tm">Team Manager</option>
                  <option value="sales">Sales Executive</option>
                  <option value="operator">Operator</option>
                </select>
              </div>
              {errors.role && <span className="error-text">{errors.role}</span>}
            </div>

            {/* PASSWORD */}
            {!isEdit && (
              <div className={`form-group ${errors.password ? "has-error" : ""}`}>
                <label>Password *</label>
                <div className="input-wrapper">
                  {/* <FaLock className="input-icon" /> */}
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder="Enter password"
                  />
                  <span
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
                {errors.password && (
                  <span className="error-text">{errors.password}</span>
                )}
              </div>
            )}

            {/* STATUS */}
            <div className="form-group">
              <label>Status</label>
              <div className="input-wrapper">
                <select name="status" value={form.status} onChange={handleChange}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

          </div>

          <div className="form-actions">
            <button className="btn-primary" disabled={loading}>
              {loading ? "Saving..." : "Save User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserLayer;
